import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Card, PrimaryButton } from "../components/common";
import { toast } from "sonner";
import { WhatsappLogo, Plus, Trash, PaperPlaneTilt, WarningCircle } from "@phosphor-icons/react";

export default function WhatsAppAlerts() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [fromNumber, setFromNumber] = useState("");
  const [newNum, setNewNum] = useState("");
  const [testNum, setTestNum] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/whatsapp/settings").then((r) => {
    setCfg(r.data); setEnabled(r.data.enabled); setRecipients(r.data.recipients || []); setFromNumber(r.data.from_number || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const addNum = () => {
    const n = newNum.trim();
    if (!n) return;
    if (!/^\+\d{8,15}$/.test(n)) { toast.error("Usa formato internacional, ej. +5215512345678"); return; }
    if (recipients.includes(n)) return;
    setRecipients([...recipients, n]); setNewNum("");
  };
  const removeNum = (n) => setRecipients(recipients.filter((x) => x !== n));

  const save = async () => {
    setSaving(true);
    try { await api.put("/whatsapp/settings", { enabled, recipients, from_number: fromNumber }); toast.success("Configuración guardada"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    const n = (testNum || "").trim();
    if (!/^\+\d{8,15}$/.test(n)) { toast.error("Número de prueba inválido (ej. +5215512345678)"); return; }
    setTesting(true);
    const fd = new FormData(); fd.append("to", n);
    try {
      const r = await api.post("/whatsapp/test", fd);
      if (r.data.ok) toast.success(`Mensaje de prueba enviado (SID ${r.data.sid})`);
      else toast.error(`No se pudo enviar: ${r.data.error || "error de Twilio"}`);
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div>
      <PageHeader overline="Notificaciones" title="Alertas WhatsApp" />

      {cfg && !cfg.configured && (
        <Card className="p-4 mb-6 border-l-4 border-l-danger flex items-start gap-3" data-testid="wa-not-configured">
          <WarningCircle size={22} weight="fill" className="text-danger mt-0.5" />
          <p className="text-sm text-muted-foreground">Twilio no está configurado en el servidor. Contacta al administrador para agregar las credenciales.</p>
        </Card>
      )}

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Envía por WhatsApp las alertas de <b className="text-foreground">contratos por vencer/vencidos</b> y <b className="text-foreground">estimaciones estancadas</b>.
        Las alertas se mandan una vez al día junto con el resumen. Con el Sandbox de Twilio, cada destinatario debe primero enviar el mensaje <b className="text-foreground">join</b> al número remitente.
      </Card>

      <Card className="p-6 max-w-2xl" data-testid="wa-settings">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><WhatsappLogo size={22} weight="fill" className="text-primary" /><span className="font-semibold">Configuración</span></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <span className={enabled ? "text-success font-semibold" : "text-muted-foreground"}>{enabled ? "Activas" : "Inactivas"}</span>
            <input type="checkbox" data-testid="wa-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-primary" />
          </label>
        </div>

        <label className="overline block mb-1">Remitente (opcional)</label>
        <input data-testid="wa-from" className="w-full h-11 px-3 rounded-sm border border-input text-sm mb-1" placeholder={cfg?.default_from || "whatsapp:+14155238886"} value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} />
        <p className="text-xs text-muted-foreground mb-5">Déjalo vacío para usar el remitente por defecto ({cfg?.default_from}).</p>

        <label className="overline block mb-1">Destinatarios</label>
        <div className="flex gap-2 mb-3">
          <input data-testid="wa-new-number" className="flex-1 h-11 px-3 rounded-sm border border-input text-sm" placeholder="+5215512345678" value={newNum} onChange={(e) => setNewNum(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNum())} />
          <button onClick={addNum} data-testid="wa-add-number" className="inline-flex items-center gap-1.5 h-11 px-4 border border-input rounded-sm text-sm font-medium hover:bg-muted transition-colors duration-200"><Plus size={16} weight="bold" /> Agregar</button>
        </div>
        {recipients.length === 0 ? <p className="text-sm text-muted-foreground mb-4">Sin destinatarios.</p> : (
          <div className="space-y-2 mb-4">
            {recipients.map((n) => (
              <div key={n} data-testid={`wa-recipient-${n}`} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm font-mono">
                {n}
                <button onClick={() => removeNum(n)} className="text-muted-foreground hover:text-danger"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        )}

        <PrimaryButton onClick={save} disabled={saving} data-testid="wa-save-btn">{saving ? "Guardando…" : "Guardar configuración"}</PrimaryButton>

        <div className="mt-6 pt-6 border-t border-border">
          <label className="overline block mb-1">Enviar mensaje de prueba</label>
          <div className="flex gap-2">
            <input data-testid="wa-test-number" className="flex-1 h-11 px-3 rounded-sm border border-input text-sm" placeholder="+5215512345678" value={testNum} onChange={(e) => setTestNum(e.target.value)} />
            <button onClick={sendTest} disabled={testing} data-testid="wa-test-btn" className="inline-flex items-center gap-1.5 h-11 px-4 bg-slate-900 text-white rounded-sm text-sm font-medium hover:bg-slate-800 transition-colors duration-200"><PaperPlaneTilt size={16} weight="bold" /> {testing ? "Enviando…" : "Probar"}</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
